import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useExcelDokument, useExcelDokumente, useSaveExcelDokument } from './data.js';
import { useToast } from '../state/toast.jsx';
import { load, save } from '../lib/storage.js';
import { newKey } from '../lib/outbox.js';
import { onEntwurf, vormerken } from '../lib/entwuerfe.js';
import { fotosErsetzen, fotosFreigeben, fotosHochladen, fotoLoeschen, lokaleFotos } from '../lib/fotospeicher.js';
import { useOnline } from './useOnline.js';

/**
 * Bearbeiten eines Excel-Dokuments (Übersichtsliste, Bewässerungsplan) – gemeinsamer Ablauf beider Editoren:
 * Stand aus Exxas laden (oder leer bei «neu»), Änderungen als Entwurf auf dem Gerät halten (übersteht Funkloch und
 * Neuladen), speichern (neu anlegen bzw. ersetzen), Konflikt erkennen, wenn jemand anderes inzwischen gespeichert hat.
 *
 * Offline: Ohne Netz (oder wenn die Sendung nicht ankommt) merkt «Speichern» den Entwurf vor; lib/entwuerfe.js speichert
 * ihn automatisch, sobald wieder Netz da ist. Man kann inzwischen weiterarbeiten. Ein neues Formular lässt sich auch
 * offline anlegen, wenn die Liste des Auftrags noch nie geladen war.
 *
 * @param {object} o
 * @param {'checklisten'|'plaene'} o.art
 * @param {string} o.nr          Auftragsnummer
 * @param {string} o.dateiId     Datei-ID oder «neu»
 * @param {string} o.base        Basis-URL des Auftrags
 * @param {string} o.pfad        Pfadteil der Seite («checkliste», «plaene»)
 * @param {string} o.entwurfPrefix  Schlüssel auf dem Gerät («checkliste», «plan»)
 * @param {(liste: { baustelle: string, team: string[] }) => object} o.leer  leeres Dokument für «neu»
 * @param {string} o.titelFehlt   Meldung, wenn der Titel fehlt
 * @param {(daten: object) => string} o.gespeichert  Text nach dem Speichern
 */
export function useExcelEntwurf({ art, nr, dateiId, base, pfad, entwurfPrefix, leer, titelFehlt, gespeichert, navState }) {
  const neu = dateiId === 'neu';
  const listenQ = useExcelDokumente(art, nr, { laden: neu });
  const dokQ = useExcelDokument(art, nr, dateiId);
  const q = neu ? listenQ : dokQ;
  const speichern = useSaveExcelDokument(art, nr);
  const navigate = useNavigate();
  const toast = useToast();
  const entwurfKey = `${entwurfPrefix}.${nr}.${dateiId}`;
  const [entwurf, setEntwurf] = useState(() => load(entwurfKey, null)); // { daten, version, vorgemerkt?, fehler? } – nur mit Änderungen
  const [key, setKey] = useState(() => load(entwurfKey, null)?.vorgemerkt?.key ?? newKey());
  const [fehler, setFehler] = useState('');
  const online = useOnline();
  const ziel = { art, nr, dateiId };

  useEffect(() => { save(entwurfKey, entwurf); }, [entwurfKey, entwurf]);

  // Nachsendung im Hintergrund (lib/entwuerfe.js): Stand vom Gerät übernehmen; ein neues Formular hat jetzt eine Datei
  useEffect(() => onEntwurf((e) => {
    if (e.key !== entwurfKey) return;
    setEntwurf(load(entwurfKey, null));
    if (e.art === 'gespeichert') {
      setKey(newKey());
      if (neu) navigate(`${base}/${pfad}/${encodeURIComponent(e.ergebnis.datei.id)}`, { replace: true, state: navState?.() });
    }
  }), [entwurfKey, neu, base, pfad, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Neu ohne Netz und ohne geladene Liste: trotzdem anfangen (Team und Baustelle fehlen dann vorerst)
  const ohneListe = !listenQ.data && (listenQ.isError || listenQ.fetchStatus === 'paused' || !online);
  const liste = listenQ.data ?? (ohneListe ? { listen: [], baustelle: '', team: [] } : null);
  const server = neu
    ? (liste ? { daten: leer(liste), datei: null, version: null, team: liste.team } : undefined)
    : dokQ.data;
  const daten = entwurf?.daten ?? server?.daten;
  const veraltet = !!(entwurf && server && (entwurf.version ?? null) !== (server.version ?? null));

  /** Änderung am Entwurf (beim ersten Tipp vom Server-Stand abgeleitet). `fn` bekommt eine Kopie und gibt sie zurück. */
  const aendern = (fn) => {
    setFehler('');
    setEntwurf((alt) => {
      const basis = alt ?? { daten: server.daten, version: server.version ?? null };
      // schon vorgemerkt: geänderter Inhalt braucht einen neuen Key (sonst käme die Antwort der alten Sendung zurück)
      const vorgemerkt = basis.vorgemerkt ? { ...basis.vorgemerkt, key: newKey() } : basis.vorgemerkt;
      return { ...basis, daten: fn(structuredClone(basis.daten)), ...(vorgemerkt ? { vorgemerkt } : {}), fehler: null };
    });
  };

  const verwerfen = () => { for (const f of lokaleFotos(entwurf?.daten)) fotoLoeschen(f.id).catch(() => {}); setEntwurf(null); setFehler(''); setKey(newKey()); if (veraltet) q.refetch(); };

  /** Auf dem Gerät vormerken – wird gespeichert, sobald Netz da ist. */
  const merken = (k, e = entwurf) => {
    setEntwurf(vormerken(entwurfKey, e, ziel, k));
    toast({ text: 'Auf dem Gerät gespeichert – wird automatisch gespeichert, sobald wieder Netz da ist', long: true });
  };

  const absenden = async () => {
    setFehler('');
    if (!entwurf.daten.titel?.trim()) { setFehler(titelFehlt); return; }
    const sendKey = entwurf.vorgemerkt?.key ?? key;
    if (!online) { merken(sendKey); return; }
    let stand = entwurf;
    try {
      // Fotos zuerst in den Auftrag; im Entwurf sofort ersetzen (bei einem späteren Fehler nicht nochmals hochladen)
      const fotos = await fotosHochladen(nr, entwurf.daten);
      if (fotos.ersetzt.size) {
        stand = { ...entwurf, daten: fotos.daten };
        setEntwurf((alt) => (alt ? { ...alt, daten: fotosErsetzen(alt.daten, fotos.ersetzt) } : alt));
        fotosFreigeben(fotos.ersetzt);
      }
      const r = await speichern.mutateAsync({ daten: fotos.daten, dateiId, version: entwurf.version, key: sendKey });
      setEntwurf(null);
      setKey(newKey());
      toast({ text: gespeichert(r.daten) });
      if (neu) { save(entwurfKey, null); navigate(`${base}/${pfad}/${encodeURIComponent(r.datei.id)}`, { replace: true, state: navState?.() }); }
    } catch (err) {
      // kein Netz, Zeitüberschreitung, Exxas pausiert, Sitzung abgelaufen: vormerken (gleicher Key → nie doppelt)
      if (err.status === 0 || err.status === 401 || err.status === 429 || err.status >= 500) { merken(sendKey, stand); return; }
      if (err.code === 'KONFLIKT') q.refetch();
      setFehler(err.message);
    }
  };

  return {
    neu, q, server, daten, entwurf, veraltet, team: server?.team ?? [], aendern, verwerfen, absenden,
    fehler: fehler || entwurf?.fehler || '', setFehler, speichert: speichern.isPending, vorgemerkt: entwurf?.vorgemerkt ?? null,
  };
}

/**
 * «Wo bin ich?» für lange Seiten: merkt sich, welcher Abschnitt gerade oben ist, und springt per Tipp hin.
 * @param {string[]} schluessel  Abschnitte in Reihenfolge
 * @returns {{ ref: (k: string) => (el: Element|null) => void, aktiv: string, springe: (k: string) => void }}
 */
export function useAbschnitte(schluessel) {
  const elemente = useRef({});
  const [aktiv, setAktiv] = useState(schluessel[0]);
  const liste = schluessel.join('|');
  useEffect(() => {
    const scroller = document.querySelector('.scroll');
    if (!scroller) return undefined;
    const pruefen = () => {
      const grenze = window.innerHeight * 0.45;
      const oben = [...liste.split('|')].reverse().find((k) => (elemente.current[k]?.getBoundingClientRect().top ?? Infinity) < grenze);
      setAktiv(oben ?? liste.split('|')[0]);
    };
    scroller.addEventListener('scroll', pruefen, { passive: true });
    pruefen();
    return () => scroller.removeEventListener('scroll', pruefen);
  }, [liste]);
  const ref = useCallback((k) => (el) => { elemente.current[k] = el; }, []);
  const springe = (k) => {
    const scroller = document.querySelector('.scroll');
    const el = elemente.current[k];
    if (scroller && el) scroller.scrollBy({ top: el.getBoundingClientRect().top - scroller.getBoundingClientRect().top - 72, behavior: 'smooth' });
  };
  return { ref, aktiv, springe };
}
